import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { toast } from 'sonner';
import type { Warehouse, WarehouseRole } from '@/types';
import { resetWarehouseScopedCache } from '@/lib/query-client';

export interface WarehouseOption {
  warehouseId: string;
  warehouseCode: string;
  warehouseName: string;
  isPrimary: boolean;
  roles: WarehouseRole[];
}

interface WarehouseState {
  warehouses: Warehouse[]; // all warehouses (for admin CRUD page)
  accessibleWarehouses: WarehouseOption[]; // user's assigned warehouses
  selectedWarehouseId: string | null;
  selectedWarehouse: Warehouse | null;
  isAllMode: boolean;

  setWarehouses: (warehouses: Warehouse[]) => void;
  setSelectedWarehouse: (warehouseId: string | null) => void;
  setAllMode: (enabled: boolean) => void;
  initFromUserProfile: (
    assignments: WarehouseOption[],
    primaryId: string | null,
    isAdmin?: boolean,
  ) => void;
  addWarehouse: (warehouse: Warehouse) => void;
  updateWarehouse: (id: string, updates: Partial<Warehouse>) => void;
  removeWarehouse: (id: string) => void;
}

export const useWarehouseStore = create<WarehouseState>()(
  persist(
    (set, get) => ({
      warehouses: [],
      accessibleWarehouses: [],
      selectedWarehouseId: null,
      selectedWarehouse: null,
      isAllMode: false,

      setWarehouses: (warehouses) => {
        const {
          selectedWarehouseId,
          selectedWarehouse: previousWarehouse,
          isAllMode,
        } = get();

        // Find the full warehouse object for the current selection in the
        // incoming list.
        const selectedWarehouse = selectedWarehouseId
          ? warehouses.find((w) => w.id === selectedWarehouseId) || null
          : null;

        // Hydration guard: if the persisted selection is NOT in the incoming
        // list (e.g. an admin deactivated the warehouse the user had
        // selected), auto-switch to the first active warehouse and inform
        // the user via toast.
        const hadSelection = !!selectedWarehouseId && !isAllMode;
        const selectionMissing = hadSelection && !selectedWarehouse;
        const fallback = selectionMissing ? warehouses[0] || null : null;

        // If no warehouse selected yet and not in All mode, default to first
        const effectiveWarehouse =
          selectedWarehouse ||
          fallback ||
          (isAllMode ? null : warehouses[0] || null);

        set({
          warehouses,
          selectedWarehouse: effectiveWarehouse,
          selectedWarehouseId: effectiveWarehouse?.id || null,
        });

        // Fire the toast AFTER state has been updated so the user sees the
        // new selection immediately when they check the switcher. Skip the
        // toast when the list is empty (rare admin edge case).
        if (selectionMissing && fallback) {
          const oldName =
            previousWarehouse?.name ?? selectedWarehouseId ?? 'Unknown';
          toast.info(
            `Your warehouse '${oldName}' was deactivated; switched to '${fallback.name}'`,
          );
        }
      },

      setSelectedWarehouse: (warehouseId) => {
        const { warehouses, selectedWarehouseId, isAllMode } = get();
        // No-op re-select guard: the switcher fires even when the current
        // value is clicked again. Same selection → same route key → no
        // remount, so clearing the cache here would strand mounted
        // observers (nothing recreates their queries).
        if (warehouseId === selectedWarehouseId && !isAllMode) return;

        const selectedWarehouse = warehouseId
          ? warehouses.find((w) => w.id === warehouseId) || null
          : null;

        // Reset BEFORE set(): the state update remounts the routed tree, and
        // the new pages' queries must be created against an already-clean
        // cache (clear() after the remount wipes the fresh queries and
        // strands mounted observers in isLoading).
        resetWarehouseScopedCache();
        set({
          selectedWarehouseId: warehouseId,
          selectedWarehouse,
          isAllMode: false,
        });
      },

      setAllMode: (enabled) => {
        if (get().isAllMode === enabled) return;
        resetWarehouseScopedCache();
        set({ isAllMode: enabled });
      },

      initFromUserProfile: (assignments, primaryId, isAdmin) => {
        const { selectedWarehouseId, isAllMode, warehouses } = get();

        // Store the accessible warehouses
        set({ accessibleWarehouses: assignments });

        // "All Warehouses" mode is admin-only. Clear any persisted isAllMode
        // for non-admins (stale state after a demotion).
        if (isAllMode && !isAdmin) {
          set({ isAllMode: false });
        }

        // If admin has explicitly chosen "All" mode, keep it
        if (isAllMode && isAdmin) {
          return;
        }

        // Determine which warehouse to select:
        // 1. If already have a valid persisted selection, keep it
        // 2. Otherwise use primary
        // 3. Otherwise use first assignment
        const validPersistedId =
          selectedWarehouseId &&
          (isAdmin ||
            assignments.some((a) => a.warehouseId === selectedWarehouseId))
            ? selectedWarehouseId
            : null;

        const targetId =
          validPersistedId || primaryId || assignments[0]?.warehouseId || null;

        const selectedWarehouse = targetId
          ? warehouses.find((w) => w.id === targetId) || null
          : null;

        set({
          selectedWarehouseId: targetId,
          selectedWarehouse,
          isAllMode: false,
        });
      },

      addWarehouse: (warehouse) => {
        const { warehouses } = get();
        set({ warehouses: [...warehouses, warehouse] });
      },

      updateWarehouse: (id, updates) => {
        const { warehouses, selectedWarehouseId } = get();
        const updatedWarehouses = warehouses.map((w) =>
          w.id === id ? { ...w, ...updates } : w,
        );

        set({
          warehouses: updatedWarehouses,
          selectedWarehouse:
            selectedWarehouseId === id
              ? updatedWarehouses.find((w) => w.id === id) || null
              : get().selectedWarehouse,
        });
      },

      removeWarehouse: (id) => {
        const { warehouses, selectedWarehouseId } = get();
        const filteredWarehouses = warehouses.filter((w) => w.id !== id);

        set({
          warehouses: filteredWarehouses,
          selectedWarehouseId:
            selectedWarehouseId === id
              ? filteredWarehouses[0]?.id || null
              : selectedWarehouseId,
          selectedWarehouse:
            selectedWarehouseId === id
              ? filteredWarehouses[0] || null
              : get().selectedWarehouse,
        });
      },
    }),
    {
      name: 'wms-warehouse',
      partialize: (state) => ({
        selectedWarehouseId: state.selectedWarehouseId,
        isAllMode: state.isAllMode,
      }),
    },
  ),
);
